import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await authAPI.verify();
      if (response.success) {
        setUser(response.user);
      } else {
        localStorage.removeItem('token');
      }
    } catch (error) {
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.verify();
      if (response.success) {
        setUser(response.user);
      }
    } catch (error) {
      // Silenciar si falla (ej. token expirado)
    }
  };

  const login = async (username, password) => {
    const result = await authAPI.login(username, password);
    if (result.success) {
      localStorage.setItem('token', result.token);
      setUser(result.user);
      return { success: true };
    }
    return {
      success: false,
      message: result.message || 'Error al iniciar sesión',
      detail: result.detail,
      status: result.status,
    };
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const esAdmin = user?.username === 'admin';

  const hasPermission = (permiso) => {
    if (esAdmin) return true;
    return user?.permisos?.includes(permiso) || false;
  };

  const hasPermissionAny = (permisos) => {
    if (!Array.isArray(permisos) || permisos.length === 0) return false;
    if (esAdmin) return true;
    return permisos.some((p) => user?.permisos?.includes(p));
  };

  const puedeGestionarGrupos = () =>
    esAdmin || hasPermissionAny(['gestionar_grupos', 'gestionar_usuarios']);

  const puedeEnviarGrupos = () =>
    esAdmin || hasPermissionAny(['enviar_grupos', 'gestionar_grupos', 'gestionar_usuarios']);

  const puedeUsarMovimientoPredefinido = (predefinidoId) => {
    if (predefinidoId == null || predefinidoId === '') return true;
    if (esAdmin) return true;
    const id = Number(predefinidoId);
    if (isNaN(id)) return false;
    const ids = user?.movimientos_predefinidos_ids;
    return Array.isArray(ids) && ids.some((x) => Number(x) === id);
  };

  const filtrarPredefinidosPermitidos = (lista) => {
    if (!Array.isArray(lista)) return [];
    if (esAdmin) return lista;
    return lista.filter((m) => puedeUsarMovimientoPredefinido(m.id));
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      hasPermission,
      hasPermissionAny,
      puedeGestionarGrupos,
      puedeEnviarGrupos,
      esAdmin,
      puedeUsarMovimientoPredefinido,
      filtrarPredefinidosPermitidos,
      loading,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

